import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PublicShell } from "../components/PublicShell";
import { cn } from "../lib/cn";

// First-party status page. Polls the backend's public status endpoint (no auth)
// every 30s. Uses /api/status so the dev proxy (/api → :3000) reaches it without
// colliding with this SPA /status page route. In dev VITE_API_ORIGIN is empty →
// relative; in prod it's the API origin.
const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "";

type ComponentStatus = "operational" | "degraded" | "down";
type ComponentKey = "api" | "database" | "cache" | "email";
interface StatusReport {
  status: ComponentStatus;
  ts: string;
  components: { key: ComponentKey; status: ComponentStatus }[];
}

async function fetchStatus(): Promise<StatusReport> {
  const res = await fetch(`${API_ORIGIN}/api/status`, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error("status_unreachable");
  return (await res.json()) as StatusReport;
}

const TONE: Record<ComponentStatus, { dot: string; text: string; bg: string; border: string }> = {
  operational: { dot: "bg-status-done", text: "text-status-done", bg: "bg-status-done/10", border: "border-status-done/30" },
  degraded: { dot: "bg-status-progress", text: "text-status-progress", bg: "bg-status-progress/10", border: "border-status-progress/30" },
  down: { dot: "bg-status-lost", text: "text-status-lost", bg: "bg-status-lost/10", border: "border-status-lost/30" },
};

function OverallIcon({ status, size = 28 }: { status: ComponentStatus; size?: number }) {
  if (status === "operational") return <CheckCircle2 size={size} className="text-status-done" />;
  if (status === "degraded") return <AlertTriangle size={size} className="text-status-progress" />;
  return <XCircle size={size} className="text-status-lost" />;
}

export function Status() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, dataUpdatedAt, refetch, isFetching } = useQuery({
    queryKey: ["service-status"],
    queryFn: fetchStatus,
    refetchInterval: 30_000,
    retry: false,
  });

  // Before the first response (incl. the prerendered shell) show a neutral
  // "checking" state, never a misleading green. If the API itself is
  // unreachable, present that as "down" — a status page that can't load is the
  // worst kind.
  const loading = isLoading && !data && !isError;
  const report: StatusReport =
    data ??
    (isError
      ? {
          status: "down",
          ts: new Date().toISOString(),
          components: (["api", "database", "cache", "email"] as ComponentKey[]).map((key) => ({ key, status: "down" as ComponentStatus })),
        }
      : { status: "operational", ts: new Date().toISOString(), components: [] });

  const overall = report.status;
  const tone = loading
    ? { dot: "bg-ink-faint", text: "text-ink-muted", bg: "bg-surface-2", border: "border-line" }
    : TONE[overall];
  const updated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "—";

  return (
    <PublicShell>
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <h1 className="text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{t("statusPage.title")}</h1>
        <p className="mt-2 text-ink-muted">{t("statusPage.subtitle")}</p>

        <div className={cn("mt-8 flex items-center gap-4 rounded-lg border p-5", tone.bg, tone.border)}>
          {loading ? <RefreshCw size={28} className="animate-spin text-ink-faint" /> : <OverallIcon status={overall} />}
          <div className="min-w-0 flex-1">
            <div className={cn("text-lg font-semibold", tone.text)}>
              {loading ? t("statusPage.checking") : t(`statusPage.overall.${overall}`)}
            </div>
            <div className="text-xs text-ink-muted">
              {loading ? t("statusPage.checking") : t("statusPage.updatedAt", { time: updated })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            title={t("statusPage.refresh")}
            className="ring-focus rounded-md border border-line bg-surface-1 p-2 text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-50"
          >
            <RefreshCw size={16} className={cn(isFetching && "animate-spin")} />
          </button>
        </div>

        <div className="mt-6 overflow-hidden rounded-lg border border-line">
          {(["api", "database", "cache", "email"] as ComponentKey[]).map((key, idx) => {
            const comp = report.components.find((c) => c.key === key);
            const st: ComponentStatus = comp?.status ?? "down";
            const ct = loading
              ? { dot: "bg-ink-faint", text: "text-ink-muted", bg: "", border: "" }
              : TONE[st];
            return (
              <div
                key={key}
                className={cn(
                  "flex items-center gap-3 bg-surface-1 px-4 py-3.5",
                  idx > 0 && "border-t border-line",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-ink">{t(`statusPage.components.${key}`)}</span>
                  <span className="block text-xs text-ink-faint">{t(`statusPage.componentsHint.${key}`)}</span>
                </span>
                <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", ct.text)}>
                  <span className={cn("h-2 w-2 rounded-full", ct.dot)} />
                  {loading ? t("statusPage.checking") : t(`statusPage.componentStatus.${st}`)}
                </span>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-xs text-ink-faint">{t("statusPage.footnote")}</p>
      </div>
    </PublicShell>
  );
}
